import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Roles } from '../../../../constants';
import { FetchStatus, ModalName, Role } from '../../../../enums';
import { useCloseModal, useModalProps } from '../../../../features/modals';
import {
  useChangeOrganizationUserRole,
  useOrganization,
  useResetChangeOrganizationUserRoleStatus,
} from '../../../../features/organization';
import { isAdmin } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';
import { IChangeUserRoleModalProps } from './types';

export const ChangeUserRoleModal = () => {
  const { t } = useTranslation();
  const {
    status: { changeUserRole: status },
  } = useOrganization();
  const changeOrganizationUserRole = useChangeOrganizationUserRole();
  const resetChangeOrganizationUserRoleStatus = useResetChangeOrganizationUserRoleStatus();
  const { user } = useModalProps(ModalName.CHANGE_USER_ROLE) as IChangeUserRoleModalProps;
  const [role, setRole] = useState<Role>(isAdmin(user) ? Role.ADMIN : Role.USER);
  const closeModal = useCloseModal(ModalName.CHANGE_USER_ROLE);

  const handleChange = (value: string) => {
    setRole(value as Role);
  };

  const remove = useCallback(() => {
    changeOrganizationUserRole({
      ...user,
      roles: [...user.roles.filter((it) => !Roles.includes(it)), role],
    });
  }, [changeOrganizationUserRole, user, role]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      closeModal();
      resetChangeOrganizationUserRoleStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button
          text={t('modal.changeUserRole.button.change')}
          loading={status === FetchStatus.LOADING}
          onClick={remove}
        />
        <Button text={t('modal.changeUserRole.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, remove, status]);

  return (
    <Modal name={ModalName.CHANGE_USER_ROLE} header={t('modal.changeUserRole.title')} footer={footer}>
      <p className="typography-paragraph-3 text-text-secondary mb-6">{t('modal.changeUserRole.description')}</p>

      <FormFields>
        <Input
          value={role}
          label={t('modal.changeUserRole.field.role.label')}
          onChange={handleChange}
          options={Roles.reduce((acc, role) => ({ ...acc, [role]: t(`common.role.${role}`) }), {})}
          placeholder={t('modal.changeUserRole.field.role.placeholder')}
          type="select"
        />
      </FormFields>
    </Modal>
  );
};
